import { sortRoomsByNumber } from './bookingUtils';

export const GROUP_VIEW_OPTIONS = ['full', 'floor', 'room_type'];
export const BOOKING_CALENDAR_GROUP_VIEW_STORAGE_KEY = 'roomnexa:dashboard:calendar-group-view:v1';
export const WALK_IN_ROOM_GROUP_VIEW_STORAGE_KEY = 'roomnexa:walkin:room-group-view:v1';

function readStoredGroupViewFromKey(storageKey, fallback = 'full') {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return GROUP_VIEW_OPTIONS.includes(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredGroupViewToKey(storageKey, groupView) {
  if (typeof window === 'undefined') return;
  if (!GROUP_VIEW_OPTIONS.includes(groupView)) return;
  try {
    window.localStorage.setItem(storageKey, groupView);
  } catch {
    // ignore
  }
}

export function readStoredGroupView(fallback = 'full') {
  return readStoredGroupViewFromKey(BOOKING_CALENDAR_GROUP_VIEW_STORAGE_KEY, fallback);
}

export function writeStoredGroupView(groupView) {
  writeStoredGroupViewToKey(BOOKING_CALENDAR_GROUP_VIEW_STORAGE_KEY, groupView);
}

export function readStoredWalkInGroupView(fallback = 'room_type') {
  return readStoredGroupViewFromKey(WALK_IN_ROOM_GROUP_VIEW_STORAGE_KEY, fallback);
}

export function writeStoredWalkInGroupView(groupView) {
  writeStoredGroupViewToKey(WALK_IN_ROOM_GROUP_VIEW_STORAGE_KEY, groupView);
}

/**
 * Group rooms by room type
 * @param {Array} rooms - Array of room objects
 * @param {Array} roomTypes - Array of room type objects
 * @returns {Object} Grouped rooms by room type ID
 */
export const groupRoomsByType = (rooms, roomTypes) => {
  const grouped = {};
  
  roomTypes.forEach(type => {
    grouped[type.id] = {
      type,
      rooms: rooms.filter(r => r.room_type_id === type.id),
    };
  });
  
  // Add rooms without type
  const untypedRooms = rooms.filter(r => !r.room_type_id);
  if (untypedRooms.length > 0) {
    grouped['none'] = {
      type: { id: 'none', name: 'Unassigned' },
      rooms: untypedRooms,
    };
  }
  
  return grouped;
};

/**
 * Group rooms by floor
 * @param {Array} rooms - Array of room objects
 * @returns {Object} Grouped rooms by floor ID
 */
export const groupRoomsByFloor = (rooms) => {
  const grouped = {};
  
  rooms.forEach(room => {
    const floorId = room.floor_id || room.floor_number || 'none';
    const floorKey = floorId !== 'none' ? floorId : 'none';
    
    if (!grouped[floorKey]) {
      grouped[floorKey] = {
        type: {
          id: floorKey,
          name: room.floor_name || (room.floor_number ? `Floor ${room.floor_number}` : 'No Floor'),
        },
        rooms: [],
      };
    }
    
    grouped[floorKey].rooms.push(room);
  });
  
  return grouped;
};

/**
 * Group all rooms together (full view)
 * @param {Array} rooms - Array of room objects
 * @returns {Object} All rooms in a single group
 */
export const groupRoomsFull = (rooms) => {
  return {
    all: {
      type: { id: 'all', name: 'All Rooms' },
      rooms: rooms,
    },
  };
};

/**
 * Group rooms based on groupView type
 * @param {Array} rooms - Array of room objects
 * @param {Array} roomTypes - Array of room type objects
 * @param {string} groupView - 'full', 'floor', or 'room_type'
 * @returns {Object} Grouped rooms
 */
export const groupRooms = (rooms, roomTypes, groupView) => {
  switch (groupView) {
    case 'room_type':
      return groupRoomsByType(rooms, roomTypes);
    case 'floor':
      return groupRoomsByFloor(rooms);
    default:
      return groupRoomsFull(rooms);
  }
};

/**
 * Get rooms of a specific type, sorted by room number
 * @param {Array} rooms - Array of room objects
 * @param {number} roomTypeId - Room type ID
 * @returns {Array} Sorted rooms of the specified type
 */
export const getRoomsByType = (rooms, roomTypeId) => {
  return sortRoomsByNumber(rooms.filter(r => r.room_type_id === roomTypeId));
};
